import { CircleNotch, FolderPlus } from "@phosphor-icons/react";
import { Button, Text } from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { useNavigate } from "@tanstack/react-router";

import { usePromoteChatToProject } from "../../../utils/queries/askPunttProjects";

// Registered route id for the project workspace. Kept as a literal (instead of
// importing campaignRoute) so this leaf component carries no route-module
// import — campaignRoute imports ./campaign, which would create a cycle.
const CAMPAIGN_ROUTE_ID = "/ask-puntt/campaigns/$projectId";

/**
 * "Create project from this chat" affordance (§2 / Journey B). Promotes a
 * standalone chat into a new AskPunttProject (idempotent server-side) and
 * navigates into the new project's feed workspace on success.
 */
export function PromoteToProjectButton({
  chatId,
  onPromoted,
}: {
  chatId: string;
  /** Optional override; defaults to navigating into the new project. */
  onPromoted?: (projectId: string) => void;
}) {
  const navigate = useNavigate();
  const promote = usePromoteChatToProject();

  const handleClick = async () => {
    try {
      const result = await promote.mutateAsync(chatId);
      const projectId = result.projectId;
      if (onPromoted) {
        onPromoted(projectId);
      } else {
        navigate({ to: CAMPAIGN_ROUTE_ID, params: { projectId } });
      }
    } catch {
      // usePromoteChatToProject surfaces an error toast.
    }
  };

  return (
    <Button
      data-testid="promote-chat-to-project-button"
      aria-label="Create project from this chat"
      size="1"
      variant="soft"
      color="gray"
      className="cursor-pointer"
      disabled={promote.isPending}
      onClick={handleClick}
    >
      {promote.isPending ? (
        <CircleNotch size={14} weight="bold" className="animate-spin" />
      ) : (
        <FolderPlus size={14} aria-hidden="true" />
      )}
      <Text size="1">Create project</Text>
    </Button>
  );
}
